/* eslint-disable camelcase */
import { SortOptionsHumanToSnakeMapping } from '../../builder/components/BuilderConstructorGrid/BuilderConstructorGrid.builder';

import { getNewQueryParamString, QueryParamBehavior } from './routing';

const sortOptionToRbIdMapping = {
  sort_relevance: 'sort_most_relevant',
  sort_bestsellers: 'sort_best_sellers',
  sort_newest: 'sort_newest',
  sort_avg_review: 'sort_top_rated',
  sort_default_unit_price_h2l: 'sort_price_high_low',
  sort_default_unit_price_l2h: 'sort_price_low_high',
  sort_retail_unit_price_h2l: 'sort_price_high_low',
  sort_retail_unit_price_l2h: 'sort_price_low_high',
};

/**
 * Converts a sort option to its corresponding resource bundle ID.
 *
 * @param {string} sortOption - The sort option to convert.
 * @returns {string} - The resource bundle ID corresponding to the sort option.
 */
export const getSortOptionRbId = (sortOption) => {
  return sortOptionToRbIdMapping[sortOption] || sortOption;
};

/**
 * Checks if the given sort option is a VIP sort option.
 * Specific to ES/Constructor fields.
 *
 * @param {string} sort_by - The sort option to check.
 * @returns {boolean} - Returns true if the sort option is a VIP sort option.
 */
export const isSortOptionVip = ({ sort_by }) => {
  return sort_by?.startsWith('default_');
};

/**
 * Checks if the given sort option is a retail sort option.
 * Specific to ES/Constructor fields.
 *
 * @param {string} sort_by - The sort option to check.
 * @returns {boolean} - Returns true if the sort option is a retail sort option.
 */
export const isSortOptionRetail = ({ sort_by }) => {
  return sort_by?.startsWith('retail_');
};

/**
 * Checks if the given sort option is a selected sort option.
 * Specific to Constructor data shape.
 *
 * @param {string} status - The status of the sort option.
 * @returns {boolean} - Returns true if the sort option is selected.
 */
export const isSortOptionSelected = ({ status }) => {
  return status === 'selected';
};

/**
 * We only want to show the sort options that are available for the current membership.
 *
 * @param {string} sort_by - The sort option to check.
 * @param {boolean} isVip - Indicates if the user is a VIP member.
 * @returns {boolean} - Returns true if the sort option is available for the current membership.
 */
export const filterSortOptionsByMembership = ({ sort_by, isVip }) => {
  if (isSortOptionVip({ sort_by })) {
    return isVip;
  }

  if (isSortOptionRetail({ sort_by })) {
    return !isVip;
  }

  return true;
};

/**
 * Filters the sort options based on the provided builderSortOptions and sortOptions.
 * If no builderSortOptions are provided, all sortOptions are returned.
 * If builderSortOptions are provided, only the options that exist in sortOptions
 * and are available for the current membership are returned.
 * The order of the options is determined by the builderSortOptions.
 *
 * @param {Array} builderSortOptions - The sort options defined in the builder.
 * @param {Array} sortOptions - The sort options available in the CIO.
 * @param {boolean} isVip - Indicates if the user is a VIP member.
 * @param {boolean} excludeSelectedOption - Indicates if the selected option should be excluded from the result.
 * @returns {Array|null} - The filtered sort options or null if no sortOptions are provided.
 */
export const getSanitizedSortOptions = ({
  builderSortOptions,
  sortOptions,
  isVip = false,
  excludeSelectedOption = false,
}) => {
  if (!sortOptions) {
    return null;
  }

  // If no builderSortOptions are provided, return all sortOptions
  // products/v2 grid sortOptions are already handled via getBuilderCollectionContent() in useProductsV2()
  // so we should just return all sortOptions here (builderSortOptions is not present for products/v2 grids)
  if (!builderSortOptions?.length) {
    return sortOptions?.filter(({ sort_by }) =>
      filterSortOptionsByMembership({ sort_by, isVip })
    );
  }

  // If builderSortOptions are provided, filter them based on the available sortOptions
  // We want to render the subset of sortOptions defined in builder (provided that they exist in sortOptions)
  // and in the order defined in builderSortOptions
  // We also want to filter out the sort options that are not available for the current membership
  const sanitizedSortOptions = builderSortOptions
    .map(({ sortOption }) => {
      return sortOptions.find(
        ({ sort_by }) => sort_by === SortOptionsHumanToSnakeMapping[sortOption]
      );
    })
    ?.filter(Boolean)
    ?.filter(({ sort_by }) =>
      filterSortOptionsByMembership({ sort_by, isVip })
    );

  if (excludeSelectedOption) {
    return sanitizedSortOptions?.filter(
      ({ status }) => !isSortOptionSelected({ status })
    );
  }

  return sanitizedSortOptions;
};

/**
 * Updates the sort option in the URL parameters, which we use as the source of truth for state management.
 * All data fetching is based on the URL parameters.
 *
 * @param {Object} router - The router object.
 * @param {string} sort - The new sort option to be added to the URL.
 * @returns {void}
 */
export const updateSortInUrlParams = ({ router, sort }) => {
  const [aspathname, query] = router.asPath.split('?');
  const newQueryParamString = getNewQueryParamString({
    query,
    newQueryParams: { sort },
    queryParamBehavior: QueryParamBehavior.REPLACE,
  });

  if (!newQueryParamString) {
    router.replace(router.pathname, router.asPath, {
      shallow: true,
    });
    return;
  }

  router.replace(router.pathname, `${aspathname}?${newQueryParamString}`, {
    shallow: true,
  });
};

export const getSelectedSortOption = (urlParams, builderSortOptions = []) => {
  const sortOption = urlParams.get('sort');
  if (sortOption) {
    return sortOption;
  }

  // Default to the first sort option in Builder if no sort option is selected,
  // mapped to the cio value
  const initialBuilderSortOption = builderSortOptions?.[0]?.sortOption;
  return SortOptionsHumanToSnakeMapping[initialBuilderSortOption];
};
